/**
 * wayai knowledge — an organization's knowledge base (#5142 step 4). This reads the invocation;
 * the work is `knowledge/`'s.
 *
 *   migrate          move the org's knowledge resources, then each hub's, into the knowledge base as
 *                    file types and files (`knowledge/migrate.ts`)
 *   refresh-preview  make the knowledge base's preview hold its production base's files
 *                    (`knowledge/refresh-preview.ts`)
 *   cut-over         switch a preview hub's agents from their knowledge resources to the
 *                    knowledge base (`knowledge/cut-over.ts`)
 *
 * Usage:
 *   wayai knowledge migrate --org <org_id> --base <knowledge base> [--hub <hub_id>] [--dry-run] [-y]
 *   wayai knowledge refresh-preview --org <org_id> --base <knowledge base> [-y]
 *   wayai knowledge cut-over --org <org_id> --base <knowledge base> --hub <preview hub_id> [--dry-run] [-y]
 */

import { parseArgs } from 'node:util';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { UUID_RE, confirm, wantsHelp } from '../lib/utils.js';
import { createDataClient } from '../data/client.js';
import { isInteractive } from '../data/helpers.js';
import { migrateKnowledge, type MigrateOptions } from '../knowledge/migrate.js';
import { hubSetup, knowledgeBase, knowledgeReader } from '../knowledge/remote.js';
import { refreshPreviewKnowledge, type RefreshOptions } from '../knowledge/refresh-preview.js';
import { cutOverHub, type CutOverOptions } from '../knowledge/cut-over.js';

const USAGE = `
wayai knowledge — the organization's knowledge base

Usage:
  wayai knowledge migrate --org <org_id> --base <knowledge base> [--hub <hub_id>] [--dry-run] [-y]
  wayai knowledge refresh-preview --org <org_id> --base <knowledge base> [-y]
  wayai knowledge cut-over --org <org_id> --base <knowledge base> --hub <preview hub_id> [--dry-run] [-y]

migrate: move knowledge resources into the organization's knowledge base.

  Without --hub, it moves the organization's knowledge resources; with --hub, that hub's own
  (read from its production hub), once the organization's have moved. Each resource becomes a
  file type, its files keep their paths, and each described folder gains an _index.md. Files are
  written create-only: one created at its path while it runs is kept, and the run stops there,
  naming it. A re-run writes what is left once that file matches its source or is moved or
  removed; until then it stops before any write, naming it. A file type created in production at
  an id it would add, after it read production, is kept too: unless it is already what the run
  would promote, the run stops before promoting, naming it.

  --base       The knowledge base: its production base, or the preview its file types are
               written in. File types are written in the preview and promoted; files are
               written in production.
  --dry-run    List what it would write, and every reason it would stop, without writing.
  -y, --yes    Skip the question before writing.

refresh-preview: make the knowledge base's preview hold its production base's files — after
  creating the preview, and whenever it should be current again. Files the preview changed are
  replaced and files only it holds are removed; nothing else on it changes, and production is
  only read. It prints what each page copied, removed and left alone; a re-run of a current
  preview changes nothing.

  --base       The production knowledge base, or its preview.
  -y, --yes    Skip the question before copying.

cut-over: switch a preview hub's agents from their knowledge resources to the knowledge base, once
  its knowledge has moved. Each distinct set of knowledge its agents read gets one read-only
  toolset on the knowledge base, reached through a Base Toolset connection; each agent is given
  list_files, read_file and its toolset's list_ and get_ tools, its resources/<slug>/ paths become
  data/<file type>/, its knowledge links are disabled, and each move is recorded. It changes the
  preview only, and prints the production steps. The preview base must hold production's files
  first (refresh-preview): the preview's agents read them there. It stops before writing on a resource placeholder
  ({{files()}}, {{resource_content()}}, {{file_content()}}) that reads knowledge an agent is about
  to lose: move that text into its instructions or a skill first.

  --hub        The preview hub.
  --base       The production knowledge base, or its preview.
  --dry-run    List every change, and every reason it would stop, without writing.
  -y, --yes    Skip the question before writing.
`;

function usageError(message: string): Error {
  return expected(`${message}\n${USAGE}`);
}

const OPTIONS = {
  org: { type: 'string' },
  base: { type: 'string' },
  hub: { type: 'string' },
  'dry-run': { type: 'boolean' },
  yes: { type: 'boolean', short: 'y' },
} as const;

function readArgs(args: string[]) {
  try {
    return parseArgs({ args, options: OPTIONS, allowPositionals: true, strict: true });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

export type KnowledgeInvocation =
  | ({ verb: 'migrate' } & MigrateOptions)
  | ({ verb: 'refresh-preview' } & RefreshOptions)
  | ({ verb: 'cut-over' } & CutOverOptions);

/** The invocation, refused before any request when it is not one this command can run. */
export function parseKnowledgeArgs(args: string[]): KnowledgeInvocation {
  const { values, positionals } = readArgs(args);
  const [verb, ...extra] = positionals;
  if (verb !== 'migrate' && verb !== 'refresh-preview' && verb !== 'cut-over') {
    throw usageError(verb === undefined ? 'No subcommand given.' : `Unknown subcommand ${JSON.stringify(verb)}.`);
  }
  if (extra.length > 0) throw usageError(`Unexpected argument ${JSON.stringify(extra[0])}.`);
  if (!values.org) throw usageError('--org <org_id> is required.');
  if (!UUID_RE.test(values.org)) throw usageError(`Invalid --org ${JSON.stringify(values.org)}: expected an organization id (a UUID).`);
  if (!values.base) throw usageError('--base <knowledge base> is required.');
  if (verb === 'refresh-preview') {
    if (values.hub !== undefined) throw usageError("--hub is migrate's and cut-over's: a refresh copies the whole knowledge base.");
    if (values['dry-run'] !== undefined) throw usageError("--dry-run is migrate's and cut-over's: a refresh has none.");
    return { verb, orgId: values.org, base: values.base, yes: values.yes === true };
  }
  if (values.hub !== undefined && !UUID_RE.test(values.hub)) {
    throw usageError(`Invalid --hub ${JSON.stringify(values.hub)}: expected a hub id (a UUID).`);
  }
  if (verb === 'cut-over') {
    if (values.hub === undefined) throw usageError('--hub <preview hub_id> is required: a cut-over is one preview hub\'s.');
    return { verb, orgId: values.org, base: values.base, hubId: values.hub, dryRun: values['dry-run'] === true, yes: values.yes === true };
  }
  return {
    verb,
    orgId: values.org,
    base: values.base,
    hubId: values.hub,
    dryRun: values['dry-run'] === true,
    yes: values.yes === true,
  };
}

export async function knowledgeCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const options = parseKnowledgeArgs(args);
  // Refused before any request: off a terminal no one can answer the question before the writes.
  if (options.verb === 'refresh-preview') {
    if (!options.yes && !isInteractive()) throw usageError('It asks before it copies; pass -y when not running in a terminal.');
    // A refresh pages for as long as the knowledge base is large: the client renews a browser login on a 401.
    const data = await createDataClient(options.orgId, { refreshLogin: true });
    await refreshPreviewKnowledge(knowledgeBase(data), options, { log: (line) => console.log(line), confirm });
    return;
  }
  if (!options.dryRun && !options.yes && !isInteractive()) {
    throw usageError('It asks before it writes; pass -y when not running in a terminal, or --dry-run.');
  }
  const { config, accessToken } = await requireAuth();
  // A migration runs for minutes, past a browser login's lifetime: both clients renew it on a 401.
  const api = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
  const data = await createDataClient(options.orgId, { refreshLogin: true });
  const io = { log: (line: string) => console.log(line), confirm };
  if (options.verb === 'cut-over') {
    await cutOverHub(hubSetup(api, options.orgId), knowledgeBase(data), options, io);
    return;
  }
  await migrateKnowledge(knowledgeReader(api, options.orgId), knowledgeBase(data), options, io);
}
